// useBuilding — PowerSync-backed hook for a single building row,
// hydrated with its floors list + parent site/base summary. Replaces
// the REST `GET /buildings/:id` call used by BuildingCaptureScreen
// during deep-link load.
//
// Returns the same `BuildingMetadata` shape that BuildingCaptureScreen
// consumes today (id, siteId, name, floc, status, floors[], parent)
// so the page swaps in with no reshape on the consumer side. `status`
// drives the StartCaptureCard resume-vs-start copy when no session is
// active for this user.
//
// Stale-data guard: navigating /capture/buildings/A → /capture/buildings/B
// keeps useQuery returning A's row until B's row arrives. The id
// freshness check suppresses A's data under B's URL during the
// transition.
//
// Frozen buildings (admin-only statuses) are deliberately NOT excluded
// from this single-building read (audit-2-#1): the LIST hooks hide them
// from navigation (use-buildings / use-sites / use-bases), but a deep
// link must LOAD the row so the capture screen renders the read-only
// frozen states (StartCaptureCard, disabled sticky CTA) — and so a
// stranded session on a just-frozen building stays CANCELLABLE from the
// field (the sticky complete/reopen CTA is disabled on frozen; a
// server-side complete still accepts). Cycle-1 #7 excluded them here;
// the row resolved to building===null with isLoading===false and the
// screen spun "Loading capture workspace…" forever, making the frozen UI
// unreachable.

import { useAllSyncStreamsHaveSynced, useQuery } from '@powersync/react'

import type { BuildingStatus } from '@citycare/shared'

import { db } from './client'

export interface UseBuildingFloor {
  id: string
  name: string
  displayOrder: number
}

export interface UseBuildingMetadata {
  id: string
  siteId: string
  name: string
  floc: string | null
  // pgEnum building_status; the PowerSync local schema mirrors it as
  // text (see hooks/powersync/schema.ts). Reads from disk fall back
  // to 'not_started' when null/missing so the consumer's render
  // branches don't have to special-case the local-mirror lag window.
  status: BuildingStatus
  floors: readonly UseBuildingFloor[]
  parent: {
    site: { id: string; name: string }
    base: { id: string; name: string }
  }
}

export interface UseBuildingResult {
  building: UseBuildingMetadata | null
  isLoading: boolean
  isFetching: boolean
  error: Error | undefined
}

interface BuildingSqlRow {
  id: string
  site_id: string
  name: string
  floc: string | null
  status: BuildingStatus | null
  // JOINs:
  site_name: string
  site_base_id: string
  base_name: string
}

interface FloorSqlRow {
  id: string
  name: string
  display_order: number
}

export function useBuilding(buildingId: string): UseBuildingResult {
  const buildingResult = useQuery<BuildingSqlRow>(
    `SELECT
       b.id,
       b.site_id,
       b.name,
       b.floc,
       b.status,
       s.name AS site_name,
       s.base_id AS site_base_id,
       bs.name AS base_name
     FROM buildings b
     INNER JOIN sites s ON b.site_id = s.id
     INNER JOIN bases bs ON s.base_id = bs.id
     WHERE b.id = ?
     LIMIT 1`,
    [buildingId],
  )

  const floorsResult = useQuery<FloorSqlRow>(
    `SELECT id, name, display_order
     FROM floors
     WHERE building_id = ?
     ORDER BY display_order ASC, name ASC`,
    [buildingId],
  )

  const referenceSiteScopedSynced = useAllSyncStreamsHaveSynced(db, [
    { name: 'reference_site_scoped', waitForStream: true },
  ])

  const buildingIsForBuildingId =
    buildingResult.data.length === 0 ||
    (buildingResult.data[0]?.id ?? '') === buildingId
  const isStale = !buildingIsForBuildingId

  const isEmptyButUnsynced =
    buildingResult.data.length === 0 && !referenceSiteScopedSynced

  const isLoading =
    buildingResult.isLoading ||
    floorsResult.isLoading ||
    isStale ||
    isEmptyButUnsynced

  let building: UseBuildingMetadata | null = null
  if (!isStale && buildingResult.data.length > 0) {
    const row = buildingResult.data[0] as BuildingSqlRow
    building = {
      id: row.id,
      siteId: row.site_id,
      name: row.name ?? '',
      floc: row.floc,
      status: row.status ?? 'not_started',
      floors: floorsResult.data.map((f) => ({
        id: f.id,
        name: f.name ?? '',
        displayOrder: f.display_order,
      })),
      parent: {
        site: { id: row.site_id, name: row.site_name ?? '' },
        base: { id: row.site_base_id, name: row.base_name ?? '' },
      },
    }
  }

  return {
    building,
    isLoading,
    isFetching: buildingResult.isFetching || floorsResult.isFetching,
    error: buildingResult.error ?? floorsResult.error,
  }
}
